import { getClient, getCustomHeaders } from '../core/global-config.js';
import { SeacloudError } from '../core/types.js';
import { getApiToken, checkAndNotifyInsufficientBalance, checkResponseBodyForInsufficientBalance, checkStreamChunkForInsufficientBalance } from '../core/config.js';

/**
 * Chat message role
 */
export type ChatMessageRole = 'system' | 'user' | 'assistant';

/**
 * Chat message interface
 */
export interface ChatMessage {
  /**
   * Message role
   */
  role: ChatMessageRole;
  /**
   * Message content
   */
  content: string;
}

/**
 * LLM chat completions request parameters
 */
export interface LlmChatCompletionsParams {
  /**
   * Model name to use for chat completion
   * Supports 100+ models including:
   * - Claude: seaart-mix-sonnet-4-5, shaseng-claude-4.5, seacloud-claude-haiku-4.5, seacloud-claude-sonnet-4, seacloud-claude-opus-4.5
   * - Kimi: kimi-k2, kimi-k2-thinking, kimi-k2-250905
   * - Grok: grok-4, grok-code-fast-1
   * - DeepSeek: deepseek-v3.1, deepseek-v3-0324, deepseek-r1, deepseek-r1-0528
   * - Gemini: gemini-2.5-pro, gemini-2.5-flash, gemini-2.0-flash, gemini-3-pro-preview, gemini-3-flash-preview
   * - Qwen: qwen-plus, qwen-plus-character, qwen3-coder-plus
   * - GLM: glm-4.5-air, glm-4.5, glm-4.6, glm-4-plus
   * - GPT: gpt-4o, gpt-4o-mini, gpt-4.1, gpt-4.1-mini, gpt-4.1-nano, gpt-5, gpt-5.1, gpt-5.2
   * - Hunyuan: hunyuan-turbos-latest, hunyuan-large-role-20250822, hunyuan-large-role-plus-20250718
   * - Mistral: mistral-small-3.2-24b-instruct, mistral-small-3.1-24b-instruct, mistral-nemo
   * - Hermes: hermes-4-70b, hermes-4-405b, hermes-3-llama-3.1-405b
   * - Doubao: doubao-romantic, doubao-seed-1-6, doubao-seed-code-preview-251028
   * - Ernie: ernie-5.0-thinking-latest, ernie-5.0-thinking-preview
   * - WizardLM: wizardlm-2-8x22b
   * - O1/O3: o1-pre, o3-pre
   * - LongCat: LongCat-Flash-Chat, LongCat-Flash-Thinking
   * - And many more...
   *
   * @example "seaart-mix-sonnet-4-5"
   * @example "kimi-k2"
   * @example "deepseek-v3.1"
   */
  model: string;

  /**
   * Array of messages comprising the conversation so far
   */
  messages: ChatMessage[];

  /**
   * Maximum number of tokens to generate in the completion
   * @default undefined (model default)
   */
  max_tokens?: number;

  /**
   * Sampling temperature (0-2)
   * Higher values make output more random, lower values more deterministic
   * @default undefined (model default, usually 1.0)
   */
  temperature?: number;

  /**
   * Nucleus sampling parameter (0-1)
   * Alternative to temperature sampling
   * @default undefined (model default, usually 1.0)
   */
  top_p?: number;

  /**
   * Whether to stream the response
   * If true, returns an async iterator of chunks
   * If false, waits for complete response
   * @default false
   */
  stream?: boolean;

  /**
   * Number of completions to generate
   * @default 1
   */
  n?: number;

  /**
   * Sequences where the API will stop generating further tokens
   */
  stop?: string | string[];

  /**
   * Penalty for new tokens based on whether they appear in the text so far (-2.0 to 2.0)
   * @default 0
   */
  presence_penalty?: number;

  /**
   * Penalty for new tokens based on their existing frequency in the text so far (-2.0 to 2.0)
   * @default 0
   */
  frequency_penalty?: number;

  /**
   * A unique identifier representing your end-user
   */
  user?: string;

  /**
   * Request timeout in milliseconds
   * Overrides the global timeout set in initSeacloud()
   * @default 30000 (from global config)
   * @example 60000 for 60 seconds timeout
   */
  timeout?: number;
}

/**
 * Usage statistics for the completion
 */
export interface ChatCompletionUsage {
  /**
   * Number of tokens in the prompt
   */
  prompt_tokens: number;

  /**
   * Number of tokens in the generated completion
   */
  completion_tokens: number;

  /**
   * Total number of tokens used
   */
  total_tokens: number;

  /**
   * Cost in USD (if available)
   */
  cost?: number;

  /**
   * Additional usage details
   */
  [key: string]: any;
}

/**
 * A single chat completion choice
 */
export interface ChatCompletionChoice {
  /**
   * The index of this choice
   */
  index: number;

  /**
   * The message generated by the model
   */
  message: ChatMessage;

  /**
   * The reason the model stopped generating tokens
   * @values stop | length | content_filter | function_call
   */
  finish_reason: string | null;

  /**
   * Additional choice-specific fields
   */
  [key: string]: any;
}

/**
 * Chat completion response (non-streaming)
 */
export interface ChatCompletionResponse {
  /**
   * Unique identifier for the completion
   */
  id: string;

  /**
   * The object type, always "chat.completion"
   */
  object: string;

  /**
   * Unix timestamp of when the completion was created
   */
  created: number;

  /**
   * The model used for completion
   */
  model: string;

  /**
   * List of completion choices
   */
  choices: ChatCompletionChoice[];

  /**
   * Usage statistics for the completion
   */
  usage?: ChatCompletionUsage;

  /**
   * Additional response fields
   */
  [key: string]: any;
}

/**
 * Delta message for streaming chunks
 */
export interface ChatCompletionChunkDelta {
  /**
   * The role of the message (only in first chunk)
   */
  role?: ChatMessageRole;

  /**
   * The content delta for this chunk
   */
  content?: string;
}

/**
 * A single streaming chunk choice
 */
export interface ChatCompletionChunkChoice {
  /**
   * The index of this choice
   */
  index: number;

  /**
   * The delta message for this chunk
   */
  delta: ChatCompletionChunkDelta;

  /**
   * The reason the model stopped generating tokens (only in last chunk)
   */
  finish_reason: string | null;

  /**
   * Additional choice-specific fields
   */
  [key: string]: any;
}

/**
 * Chat completion streaming chunk
 */
export interface ChatCompletionChunk {
  /**
   * Unique identifier for the completion
   */
  id: string;

  /**
   * The object type, always "chat.completion.chunk"
   */
  object: string;

  /**
   * Unix timestamp of when the chunk was created
   */
  created: number;

  /**
   * The model used for completion
   */
  model: string;

  /**
   * List of chunk choices
   */
  choices: ChatCompletionChunkChoice[];

  /**
   * Additional chunk fields
   */
  [key: string]: any;
}

/**
 * Call LLM chat completions API (non-streaming)
 *
 * @example
 * ```typescript
 * import { initSeacloud, llmChatCompletions } from 'seacloud-sdk';
 *
 * initSeacloud({ apiKey: 'your-api-key' });
 *
 * const response = await llmChatCompletions({
 *   model: 'seaart-mix-sonnet-4-5',
 *   messages: [
 *     { role: 'user', content: 'Hello!' }
 *   ],
 *   max_tokens: 100
 * });
 *
 * console.log(response.choices[0].message.content);
 * ```
 *
 * @example With custom timeout
 * ```typescript
 * // Set 60 seconds timeout for this specific call
 * const response = await llmChatCompletions({
 *   model: 'deepseek-v3.1',
 *   messages: [
 *     { role: 'user', content: 'Write a long article' }
 *   ],
 *   timeout: 60000  // 60 seconds
 * });
 * ```
 *
 * @param params Request parameters
 * @returns Chat completion response
 */
export async function llmChatCompletions(
  params: LlmChatCompletionsParams & { stream?: false }
): Promise<ChatCompletionResponse>;

/**
 * Call LLM chat completions API (streaming)
 *
 * @example
 * ```typescript
 * import { initSeacloud, llmChatCompletions } from 'seacloud-sdk';
 *
 * initSeacloud({ apiKey: 'your-api-key' });
 *
 * const stream = await llmChatCompletions({
 *   model: 'seaart-mix-sonnet-4-5',
 *   messages: [
 *     { role: 'user', content: 'Tell me a story' }
 *   ],
 *   stream: true
 * });
 *
 * for await (const chunk of stream) {
 *   const content = chunk.choices[0]?.delta?.content;
 *   if (content) {
 *     process.stdout.write(content);
 *   }
 * }
 * ```
 *
 * @param params Request parameters with stream: true
 * @returns Async iterator of chat completion chunks
 */
export async function llmChatCompletions(
  params: LlmChatCompletionsParams & { stream: true }
): Promise<AsyncIterable<ChatCompletionChunk>>;

// Implementation
export async function llmChatCompletions(
  params: LlmChatCompletionsParams
): Promise<ChatCompletionResponse | AsyncIterable<ChatCompletionChunk>> {
  const client = await getClient();
  const config = client.getConfig();

  const url = `${config.baseUrl}/llm/chat/completions`;

  // 动态获取 API token
  const token = await getApiToken(config.apiKey);

  // 获取全局自定义 headers
  const customHeaders = getCustomHeaders();

  // Use params.timeout if provided, otherwise use global config.timeout
  const timeoutMs = params.timeout ?? config.timeout;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await config.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...customHeaders, // 合并自定义 headers
      },
      body: JSON.stringify(params),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorBody = await response.text();
      const err = new SeacloudError(
        `HTTP ${response.status}: ${errorBody}`,
        response.status,
        errorBody,
      );
      checkAndNotifyInsufficientBalance(err);
      throw err;
    }

    // Streaming response
    if (params.stream) {
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const result = await response.json();
        checkResponseBodyForInsufficientBalance(result);
        return result as ChatCompletionResponse;
      }
      return parseStreamingResponse(response);
    }

    // Non-streaming response
    const result = await response.json();
    checkResponseBodyForInsufficientBalance(result);
    return result as ChatCompletionResponse;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof SeacloudError) {
      checkAndNotifyInsufficientBalance(error);
      throw error;
    }

    if ((error as Error).name === 'AbortError') {
      throw new SeacloudError(`Request timeout after ${timeoutMs}ms`);
    }

    throw new SeacloudError(
      `Request failed: ${(error as Error).message}`,
      undefined,
      error,
    );
  }
}

/**
 * Parse streaming response into async iterable
 */
async function* parseStreamingResponse(
  response: Response
): AsyncIterable<ChatCompletionChunk> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new SeacloudError('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      // Keep the last incomplete line in buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();

        if (!trimmedLine) continue;
        if (trimmedLine === 'data: [DONE]') continue;

        if (trimmedLine.startsWith('data: ')) {
          const jsonStr = trimmedLine.slice(6);
          try {
            const chunk = JSON.parse(jsonStr) as ChatCompletionChunk;
            checkStreamChunkForInsufficientBalance(chunk);
            yield chunk;
          } catch (error) {
            console.warn('Failed to parse SSE chunk:', jsonStr);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
